import { useEffect, useMemo, useState } from "react";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { useMaterialUIController } from "context";
import { createGoal, deleteGoal, getGoals, updateGoal } from "layouts/goals/goalsApi";

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const getMonthKey = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const getReferenceDate = (monthKey) => `${monthKey}-15T12:00:00`;

const toNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatCurrency = (value) => currencyFormatter.format(toNumber(value));

const normalizeGoal = (goal) => ({
  id: goal.id,
  name: goal.name ?? "",
  targetValue: Number(goal.targetValue ?? 0).toFixed(2),
  savedValue: Number(goal.savedValue ?? 0).toFixed(2),
});

function SummaryCard({ icon, title, value, detail, color }) {
  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={2} display="flex" gap={1.5} alignItems="center">
        <MDBox
          sx={{
            width: 42,
            height: 42,
            borderRadius: "10px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            backgroundColor: `rgba(${color}, 0.14)`,
            color: `rgb(${color})`,
          }}
        >
          <Icon>{icon}</Icon>
        </MDBox>
        <MDBox minWidth={0}>
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {title}
          </MDTypography>
          <MDTypography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
            {value}
          </MDTypography>
          {detail && (
            <MDTypography variant="caption" color="text">
              {detail}
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
    </Card>
  );
}

function Goals() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [selectedMonth, setSelectedMonth] = useState(getMonthKey());
  const [goals, setGoals] = useState([]);
  const [goalDraft, setGoalDraft] = useState({ name: "", targetValue: "", savedValue: "" });
  const [openSnackMessage, setOpenSnackMessage] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadGoals = async () => {
      try {
        const data = await getGoals(getReferenceDate(selectedMonth));

        if (!isMounted) {
          return;
        }

        setGoals(Array.isArray(data) ? data.map(normalizeGoal) : []);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setGoals([]);
        setOpenSnackMessage({ type: "error", message: `${error}` });
      }
    };

    loadGoals();

    return () => {
      isMounted = false;
    };
  }, [selectedMonth]);

  const savedForGoals = useMemo(
    () => goals.reduce((total, goal) => total + toNumber(goal.savedValue), 0),
    [goals]
  );
  const targetForGoals = useMemo(
    () => goals.reduce((total, goal) => total + toNumber(goal.targetValue), 0),
    [goals]
  );

  const handleSelectedMonthChange = (monthKey) => {
    setSelectedMonth(monthKey || getMonthKey());
    setGoalDraft({ name: "", targetValue: "", savedValue: "" });
  };

  const handleAddGoal = async () => {
    if (!goalDraft.name.trim() || toNumber(goalDraft.targetValue) <= 0) {
      setOpenSnackMessage({ type: "warning", message: "Informe nome e valor da meta." });
      return;
    }

    try {
      const savedGoal = await createGoal({
        referenceMonth: getReferenceDate(selectedMonth),
        name: goalDraft.name.trim(),
        targetValue: toNumber(goalDraft.targetValue),
        savedValue: toNumber(goalDraft.savedValue),
      });

      setGoals((currentGoals) => [...currentGoals, normalizeGoal(savedGoal)]);
      setGoalDraft({ name: "", targetValue: "", savedValue: "" });
      setOpenSnackMessage({ type: "success", message: "Meta adicionada." });
    } catch (error) {
      setOpenSnackMessage({ type: "error", message: `${error}` });
    }
  };

  const handleUpdateGoal = (goalId, field, value) => {
    setGoals((currentGoals) =>
      currentGoals.map((goal) =>
        goal.id === goalId
          ? {
              ...goal,
              [field]: value,
            }
          : goal
      )
    );
  };

  const handlePersistGoal = async (goalId) => {
    const goal = goals.find((currentGoal) => currentGoal.id === goalId);

    if (!goal) {
      return;
    }

    if (!goal.name.trim() || toNumber(goal.targetValue) <= 0) {
      setOpenSnackMessage({ type: "warning", message: "Informe nome e valor da meta." });
      return;
    }

    try {
      const savedGoal = await updateGoal(goalId, {
        referenceMonth: getReferenceDate(selectedMonth),
        name: goal.name.trim(),
        targetValue: toNumber(goal.targetValue),
        savedValue: toNumber(goal.savedValue),
      });

      setGoals((currentGoals) =>
        currentGoals.map((currentGoal) => (currentGoal.id === goalId ? normalizeGoal(savedGoal) : currentGoal))
      );
    } catch (error) {
      setOpenSnackMessage({ type: "error", message: `${error}` });
    }
  };

  const handleDeleteGoal = async (goalId) => {
    try {
      await deleteGoal(goalId);

      setGoals((currentGoals) => currentGoals.filter((goal) => goal.id !== goalId));
      setOpenSnackMessage({ type: "success", message: "Meta removida." });
    } catch (error) {
      setOpenSnackMessage({ type: "error", message: `${error}` });
    }
  };

  return (
    <DashboardLayout>
      <Grid item xs={12} sm={6} lg={3}>
        <MDSnackbar
          color={openSnackMessage?.type}
          icon="check"
          title={openSnackMessage?.message}
          open={Boolean(openSnackMessage)}
          onClose={() => setOpenSnackMessage(null)}
          close={() => setOpenSnackMessage(null)}
        />
      </Grid>
      <DashboardNavbar />

      <MDBox mt={3}>
        <MDBox
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          gap={2}
          flexDirection={{ xs: "column", md: "row" }}
        >
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold">
              Metas
            </MDTypography>
            <MDTypography variant="button" color="text">
              Metas do mês selecionado.
            </MDTypography>
          </MDBox>
          <TextField
            type="month"
            label="Mês"
            value={selectedMonth}
            onChange={(event) => handleSelectedMonthChange(event.target.value)}
            sx={{
              width: { xs: "100%", md: 190 },
              "& input::-webkit-calendar-picker-indicator": {
                filter: darkMode ? "invert(1)" : "none",
                opacity: 0.8,
              },
            }}
            InputLabelProps={{ shrink: true }}
          />
        </MDBox>

        <Card>
          <MDBox p={3}>
            <MDBox display="flex" alignItems="center" gap={1}>
              <Icon color="info">flag</Icon>
              <MDTypography variant="h6" fontWeight="medium">
                Metas do mês
              </MDTypography>
            </MDBox>

            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Meta"
                  value={goalDraft.name}
                  onChange={(event) => setGoalDraft((currentDraft) => ({ ...currentDraft, name: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Valor alvo"
                  value={goalDraft.targetValue}
                  onChange={(event) =>
                    setGoalDraft((currentDraft) => ({ ...currentDraft, targetValue: event.target.value }))
                  }
                  inputProps={{ min: 0, step: "0.01" }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Guardado"
                  value={goalDraft.savedValue}
                  onChange={(event) =>
                    setGoalDraft((currentDraft) => ({ ...currentDraft, savedValue: event.target.value }))
                  }
                  inputProps={{ min: 0, step: "0.01" }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <MDButton fullWidth variant="gradient" color="info" onClick={handleAddGoal} sx={{ height: "100%" }}>
                  Adicionar
                </MDButton>
              </Grid>
            </Grid>

            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} md={4}>
                <SummaryCard
                  icon="savings"
                  title="Guardado em metas"
                  value={formatCurrency(savedForGoals)}
                  detail={`Alvo: ${formatCurrency(targetForGoals)}`}
                  color="26, 115, 232"
                />
              </Grid>
              {goals.map((goal) => {
                const goalProgress =
                  toNumber(goal.targetValue) > 0
                    ? Math.min((toNumber(goal.savedValue) / toNumber(goal.targetValue)) * 100, 100)
                    : 0;

                return (
                  <Grid item xs={12} md={4} key={goal.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        border: `1px solid ${darkMode ? "rgba(148, 163, 184, 0.18)" : "rgba(52, 71, 103, 0.12)"}`,
                        boxShadow: "none",
                      }}
                    >
                      <MDBox p={2}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                          <MDTypography variant="button" fontWeight="bold">
                            {goal.name}
                          </MDTypography>
                          <MDButton variant="text" color="error" size="small" onClick={() => handleDeleteGoal(goal.id)}>
                            Remover
                          </MDButton>
                        </MDBox>
                        <MDBox mt={1.5}>
                          <LinearProgress
                            variant="determinate"
                            value={goalProgress}
                            color={goalProgress >= 100 ? "success" : "info"}
                          />
                        </MDBox>
                        <Grid container spacing={1.5} mt={1}>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Alvo"
                              value={goal.targetValue}
                              onChange={(event) => handleUpdateGoal(goal.id, "targetValue", event.target.value)}
                              onBlur={() => handlePersistGoal(goal.id)}
                              inputProps={{ min: 0, step: "0.01" }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Guardado"
                              value={goal.savedValue}
                              onChange={(event) => handleUpdateGoal(goal.id, "savedValue", event.target.value)}
                              onBlur={() => handlePersistGoal(goal.id)}
                              inputProps={{ min: 0, step: "0.01" }}
                            />
                          </Grid>
                        </Grid>
                      </MDBox>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </MDBox>
        </Card>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default Goals;
